'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { Inbox, Loader2 } from 'lucide-react';
import DataEmpty from '@/components/DataEmpty';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import useChartAutoResize from '@/hooks/use-chart-auto-resize';

interface EChartProps {
  option: echarts.EChartsOption | null;
  loading?: boolean;
  empty?: boolean;
  className?: string;
  wrapperClassName?: string;
  openInSidebar?: boolean;
  emptyMessage?: string;
  notMerge?: boolean;
  loadingFallback?: ReactNode;
  emptyFallback?: ReactNode;
  onChartReady?: (chart: ECharts, element: HTMLDivElement) => void | (() => void);
}

export default function EChart({
  option,
  loading = false,
  empty = false,
  className,
  wrapperClassName,
  openInSidebar,
  emptyMessage,
  notMerge = true,
  loadingFallback,
  emptyFallback,
  onChartReady,
}: EChartProps) {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  useChartAutoResize(chartInstanceRef, openInSidebar);

  useEffect(() => {
    if (!chartRef.current) return;

    if (loading) {
      return;
    }

    if (empty || !option) {
      chartInstanceRef.current?.clear();
      return;
    }

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;
    chart.setOption(option, notMerge);
    chart.resize();

    return onChartReady?.(chart, chartRef.current);
  }, [empty, loading, notMerge, onChartReady, option]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  return (
    <div className={cn('relative', wrapperClassName ?? 'min-h-65')}>
      <div
        ref={chartRef}
        className={cn('w-full', className ?? 'min-h-65', {
          'opacity-0': !loading && (empty || !option),
        })}
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          {loadingFallback ?? <Loader2 size={40} className="animate-spin" stroke="#8B3FFC" />}
        </div>
      )}
      {!loading &&
        (empty || !option) &&
        (emptyFallback ?? (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg border border-[#3A3A3A]">
            <DataEmpty
              image={<Inbox className="text-[#757575]" />}
              message={emptyMessage ?? t('No data available')}
              classNames={{ wrapper: 'flex-row' }}
            />
          </div>
        ))}
    </div>
  );
}
